import { useEffect, useState, type PropsWithChildren } from "react"
import { ArrowUpRight, Menu, X } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import { Link, NavLink, useLocation } from "react-router-dom"
import { useSiteConfig } from "../content/PreviewProvider"

export function Seo({
  title,
  description,
  image = "/og-card.jpg",
}: {
  title: string
  description?: string
  image?: string
}) {
  const site = useSiteConfig()
  const { pathname } = useLocation()
  const resolvedDescription = description ?? site.description

  useEffect(() => {
    const fullTitle = title.includes("Leah Harding") ? title : `${title} — Leah Harding`
    document.title = fullTitle
    const setMeta = (selector: string, value: string) => {
      const element = document.querySelector<HTMLMetaElement>(selector)
      if (element) element.content = value
    }
    setMeta('meta[name="description"]', resolvedDescription)
    setMeta('meta[property="og:title"]', fullTitle)
    setMeta('meta[property="og:description"]', resolvedDescription)
    setMeta('meta[property="og:url"]', `https://leahharding.com${pathname}`)
    setMeta('meta[property="og:image"]', `https://leahharding.com${image}`)
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (canonical) canonical.href = `https://leahharding.com${pathname}`
  }, [resolvedDescription, image, pathname, title])

  return null
}

export function SiteShell({ children }: PropsWithChildren) {
  const site = useSiteConfig()
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    setMenuOpen(false)
    window.scrollTo({ top: 0, behavior: "instant" })
  }, [location.pathname])

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main">Skip to content</a>
      <header className="site-header">
        <Link className="wordmark" to="/" aria-label="Leah Harding, home">
          <span>LH</span>
          <span className="wordmark-name">Leah Harding</span>
        </Link>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {site.navigation.map((item) => (
            <NavLink
              className={({ isActive }) => (isActive ? "active" : "")}
              end={item.href === "/"}
              key={item.href}
              to={item.href}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <Link className="header-cta" to={site.headerCta.href}>
          {site.headerCta.label} <ArrowUpRight size={16} />
        </Link>
        <button
          aria-expanded={menuOpen}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          className="menu-toggle"
          onClick={() => setMenuOpen((open) => !open)}
          type="button"
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
      </header>
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            animate={{ opacity: 1, y: 0 }}
            className="mobile-nav"
            exit={{ opacity: 0, y: -18 }}
            initial={{ opacity: 0, y: -18 }}
          >
            {site.navigation.map((item, index) => (
              <motion.div
                animate={{ opacity: 1, x: 0 }}
                initial={{ opacity: 0, x: -20 }}
                key={item.href}
                transition={{ delay: index * 0.05 }}
              >
                <NavLink to={item.href}>{item.label}</NavLink>
              </motion.div>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
      <main id="main">{children}</main>
      <footer className="site-footer">
        <div>
          <p className="kicker">{site.footer.kicker}</p>
          <h2>{site.footer.heading}</h2>
          <Link className="text-link light" to={site.footer.cta.href}>
            {site.footer.cta.label} <ArrowUpRight />
          </Link>
        </div>
        <div className="footer-meta">
          <div className="footer-links">
            {site.footer.links.map((item) => (
              <a href={item.href} key={`${item.label}-${item.href}`} rel="noreferrer" target="_blank">
                {item.label}
              </a>
            ))}
          </div>
          <p className="footer-copyright">© {new Date().getFullYear()} {site.footer.copyrightName}</p>
          <p className="footer-credit">
            <a href={site.footer.credit.href} rel="noreferrer" target="_blank">
              {site.footer.credit.label}
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}
